import { Trans } from "@lingui/react/macro";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import AppLayout from "@/components/AppLayout";
import BackToBookingButton from "@/components/BackToBookingButton";

const faqItems = [
  {
    id: "cancel-booking",
    question: <Trans>How do I cancel my booking?</Trans>,
    answer: (
      <Trans>
        If you wish to cancel your booking, please contact the campsite directly. You'll find the
        campsite's email address in your booking confirmation.
      </Trans>
    ),
  },
  {
    id: "cancel-refund",
    question: <Trans>If I cancel, will I receive a complete refund?</Trans>,
    answer: (
      <Trans>
        The amount you will be refunded depends on the campsite's cancellation policy and at what
        point you cancel your booking. If you cancel within 7 days, you will receive a complete
        refund. If you cancel after 7 days, the amount you'll receive from the campsite depends on
        their cancellation policy. To find out what their cancellation policy is, please contact the
        campsite directly. You'll find their email address in your booking confirmation. Please note
        that part of the amount may be deducted to cover costs incurred by handling the booking
        process.
      </Trans>
    ),
  },
  {
    id: "change-booking",
    question: <Trans>How can I change my booking?</Trans>,
    answer: (
      <Trans>
        Please contact the campsite directly. You'll find the campsite's email address in your
        booking confirmation. Please note that there are limited capacities and that changes in
        dates and/or the number of guests could affect the total price.
      </Trans>
    ),
  },
  {
    id: "no-confirmation",
    question: <Trans>I didn't receive a booking confirmation. What should I do now?</Trans>,
    answer: (
      <Trans>
        First, check your email's spam folder. If the booking confirmation isn't there either,
        please contact <a href="mailto:support@campnerd.camp">support@campnerd.camp</a>. Often a
        simple typo can be the culprit.
      </Trans>
    ),
  },
  {
    id: "payment-failed",
    question: <Trans>The payment transaction failed. What do I do now?</Trans>,
    answer: (
      <Trans>
        Normally, you'll receive an automatic refund. The error message should tell you if this is
        the case. If this didn't work, please contact{" "}
        <a href="mailto:support@campnerd.camp">support@campnerd.camp</a>.
      </Trans>
    ),
  },
  {
    id: "no-payment-method",
    question: (
      <Trans>I don't have any of the suggested payment methods. How can I make a booking?</Trans>
    ),
    answer: (
      <Trans>
        In order to guarantee a secure transaction, the Online Booking feature only works with the
        suggested payment methods. You can, however, send an enquiry to the campsite directly; their
        contact information can be found on the campsite's detail page.
      </Trans>
    ),
  },
  {
    id: "invoice",
    question: <Trans>Will I get an invoice?</Trans>,
    answer: (
      <Trans>
        The invoice will be drawn up by the campsite, so you should contact them about it directly.
        You'll find the campsite's email address in your booking confirmation.
      </Trans>
    ),
  },
  {
    id: "outside-hours",
    question: <Trans>Can I arrive outside of opening hours?</Trans>,
    answer: (
      <Trans>
        That's entirely up to the campsite, so you should contact them about it directly. You'll
        find the campsite's email address in your booking confirmation.
      </Trans>
    ),
  },
  {
    id: "specific-pitch",
    question: <Trans>Can I book a specific pitch/accommodation?</Trans>,
    answer: (
      <Trans>
        Unfortunately, it is currently not possible to book a specific pitch/accommodation. Should
        you have any special requests, you can inform the campsite in the "Your message to the
        campsite" section.
      </Trans>
    ),
  },
  {
    id: "more-questions",
    question: <Trans>I have more questions about the campsite. Where should I turn?</Trans>,
    answer: (
      <Trans>
        You'll find lots of information such as pictures, videos, features and reviews here on{" "}
        <a href="https://www.campingplatz-deutschland.de/en.html">Campingplatz-Deutschland.de</a>.
        If you still have any specific questions, you should contact the campsite directly. Their
        staff will surely be glad to help you. You'll find the campsite's email address in your
        booking confirmation.
      </Trans>
    ),
  },
  {
    id: "currency-conversion",
    question: <Trans>How does the currency conversion work?</Trans>,
    answer: (
      <Trans>
        Payments are always made in the campsite's currency. Should you use a different currency
        when you pay, an additional conversion fee will apply. The fee is 1.2% of the deposit for
        the following currencies: CHF, SEK, BRL, PLN, EUR, DKK, CZK, ZAR, SGD, JPY, CAD, HKD, USD,
        NOK, GBP, NZD and AUD. The conversion fee for all other currencies is 3%.
      </Trans>
    ),
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : false);
  };

  return (
    <AppLayout contentMaxWidth="md">
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <BackToBookingButton />
        <Typography variant="h2" gutterBottom sx={{ mb: 1 }}>
          <Trans>Frequently Asked Questions</Trans>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          <Trans>
            Here you'll find answers to the most frequently asked questions about the online booking
            process. If you have a question that isn't answered here, please contact our Support
            Team.
          </Trans>
        </Typography>
        <Stack spacing={1.5}>
          {faqItems.map((item) => (
            <Accordion
              key={item.id}
              expanded={expanded === item.id}
              onChange={handleChange(item.id)}
              disableGutters
              elevation={0}
              sx={{
                borderRadius: "20px !important",
                "&::before": { display: "none" },
                py: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: { xs: 2, sm: 3 }, py: 0.5 }}
              >
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, pt: 0 }}>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>
    </AppLayout>
  );
}
